import { useState, useCallback, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { 
  Terminal, 
  Search, 
  Download, 
  Globe, 
  WifiOff, 
  MapPin, 
  Phone, 
  Star, 
  Clock, 
  ExternalLink,
  Database,
  FileSpreadsheet,
  FileText,
  Filter,
  Loader2,
  CheckCircle2,
  XCircle,
  Facebook,
  Instagram,
  Copy,
  ClipboardCheck,
  Mail,
  MessageCircle,
  SearchIcon,
  LogOut,
  Shield,
  User,
  Edit2,
  Check,
  X,
  Plus,
  Send,
  Zap
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue,
  SelectGroup,
  SelectLabel
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Toaster, toast } from "sonner";
import Login from "@/components/Login";
import AdminPanel from "@/components/AdminPanel";
import BulkEmailCampaign from "@/components/BulkEmailCampaign";
import SendEmailModal from "@/components/SendEmailModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NICHE_GROUPS = [
  {
    label: "Food & Hospitality",
    items: [
      { value: "restaurants", label: "Restaurants (All)" },
      { value: "chinese restaurants", label: "Chinese Restaurants" },
      { value: "thai restaurants", label: "Thai Restaurants" },
      { value: "indian restaurants", label: "Indian Restaurants" },
      { value: "italian restaurants", label: "Italian Restaurants" },
      { value: "mexican restaurants", label: "Mexican Restaurants" },
      { value: "japanese restaurants", label: "Japanese Restaurants" },
      { value: "korean restaurants", label: "Korean Restaurants" },
      { value: "vietnamese restaurants", label: "Vietnamese Restaurants" },
      { value: "greek restaurants", label: "Greek Restaurants" },
      { value: "turkish restaurants", label: "Turkish Restaurants" },
      { value: "lebanese restaurants", label: "Lebanese Restaurants" },
      { value: "spanish restaurants", label: "Spanish Restaurants" },
      { value: "french restaurants", label: "French Restaurants" },
      { value: "american restaurants", label: "American Restaurants" },
      { value: "burger restaurants", label: "Burger Joints" },
      { value: "pizza restaurants", label: "Pizza Restaurants" },
      { value: "sushi restaurants", label: "Sushi Restaurants" },
      { value: "steakhouse", label: "Steakhouses" },
      { value: "seafood restaurants", label: "Seafood Restaurants" },
      { value: "vegan restaurants", label: "Vegan Restaurants" },
      { value: "vegetarian restaurants", label: "Vegetarian Restaurants" },
      { value: "fish and chips", label: "Fish & Chips" },
      { value: "caribbean restaurants", label: "Caribbean Restaurants" },
      { value: "african restaurants", label: "African Restaurants" },
      { value: "persian restaurants", label: "Persian Restaurants" },
      { value: "gastro pubs", label: "Gastro Pubs" },
      { value: "pubs with food", label: "Pubs with Food" },
      { value: "cafes", label: "Cafes" },
      { value: "park cafes", label: "Park Cafes" },
      { value: "garden centre cafes", label: "Garden Centre Cafes" },
      { value: "museum cafes", label: "Museum Cafes" },
      { value: "food trucks", label: "Food Trucks" },
      { value: "pop up street food stalls", label: "Pop-Up Street Food Stalls" },
      { value: "caterers", label: "Caterers" },
      { value: "bakeries", label: "Bakeries" },
      { value: "butchers", label: "Butchers" },
    ]
  },
  {
    label: "Council & Government",
    items: [
      { value: "food safety officer", label: "Food Safety Officers (EHO's)", mode: "people" },
      { value: "planning officer", label: "Planning Officers", mode: "people" },
      { value: "licensing officer", label: "Licensing Officers", mode: "people" },
      { value: "building control officer", label: "Building Control Officers", mode: "people" },
      { value: "trading standards officer", label: "Trading Standards Officers", mode: "people" },
    ]
  },
  {
    label: "People Search (Directors & Managers)",
    items: [
      { value: "restaurant manager", label: "Restaurant Managers", mode: "people" },
      { value: "cafe owner", label: "Cafe Owners / Managers", mode: "people" },
      { value: "hotel manager", label: "Hotel Managers", mode: "people" },
      { value: "pub landlord", label: "Pub Landlords", mode: "people" },
      { value: "catering manager", label: "Catering Managers", mode: "people" },
      { value: "company director", label: "Company Directors", mode: "people" },
      { value: "business owner", label: "Business Owners", mode: "people" },
    ]
  },
  {
    label: "Leisure & Recreation",
    items: [
      { value: "leisure centres", label: "Leisure Centres" },
      { value: "sports centres", label: "Sports Centres" },
      { value: "swimming pools", label: "Swimming Pools" },
      { value: "gyms", label: "Gyms" },
      { value: "fitness centres", label: "Fitness Centres" },
      { value: "health clubs", label: "Health Clubs" },
      { value: "recreation centres", label: "Recreation Centres" },
      { value: "community centres", label: "Community Centres" },
      { value: "bowling alleys", label: "Bowling Alleys" },
      { value: "golf clubs", label: "Golf Clubs" },
      { value: "tennis clubs", label: "Tennis Clubs" },
      { value: "football clubs", label: "Football Clubs (All)" },
      { value: "amateur football clubs", label: "Amateur Football Clubs" },
      { value: "sunday league football", label: "Sunday League Football" },
      { value: "5 a side football", label: "5-a-Side Football Venues" },
      { value: "football leagues", label: "Football Leagues" },
      { value: "cricket clubs", label: "Cricket Clubs" },
      { value: "rugby clubs", label: "Rugby Clubs" },
      { value: "hockey clubs", label: "Hockey Clubs" },
      { value: "netball clubs", label: "Netball Clubs" },
      { value: "basketball clubs", label: "Basketball Clubs" },
      { value: "athletics clubs", label: "Athletics Clubs" },
      { value: "running clubs", label: "Running Clubs" },
      { value: "cycling clubs", label: "Cycling Clubs" },
      { value: "boxing clubs", label: "Boxing Clubs" },
      { value: "martial arts clubs", label: "Martial Arts Clubs" },
    ]
  },
  {
    label: "Local Services",
    items: [
      { value: "plumbers", label: "Plumbers" },
      { value: "electricians", label: "Electricians" },
      { value: "locksmiths", label: "Locksmiths" },
      { value: "pest control", label: "Pest Control" },
      { value: "carpet cleaning", label: "Carpet Cleaning" },
      { value: "window cleaning", label: "Window Cleaning" },
      { value: "pressure washing", label: "Pressure Washing" },
      { value: "garage door repair", label: "Garage Door Repair" },
      { value: "appliance repair", label: "Appliance Repair" },
      { value: "painters", label: "Painters" },
      { value: "fencing contractors", label: "Fencing Contractors" },
      { value: "tree services", label: "Tree Services" },
      { value: "rubbish clearance", label: "Rubbish Clearance" },
      { value: "cleaning services", label: "Cleaning Services" },
      { value: "landscaping", label: "Landscaping" },
      { value: "roofing", label: "Roofing" },
      { value: "hvac", label: "HVAC" },
    ]
  },
  {
    label: "Tradespeople",
    items: [
      { value: "bricklayers", label: "Bricklayers" },
      { value: "plasterers", label: "Plasterers" },
      { value: "tilers", label: "Tilers" },
      { value: "carpenters", label: "Carpenters" },
      { value: "welders", label: "Welders" },
      { value: "mechanics", label: "Auto Mechanics" },
    ]
  },
  {
    label: "Fitness & Wellness",
    items: [
      { value: "fitness instructors", label: "Fitness Instructors" },
      { value: "yoga studios", label: "Yoga Studios" },
      { value: "pilates studios", label: "Pilates Studios" },
      { value: "spin classes", label: "Spin Classes" },
      { value: "personal trainers", label: "Personal Trainers" },
      { value: "massage therapists", label: "Massage Therapists" },
      { value: "physiotherapists", label: "Physiotherapists" },
      { value: "chiropractors", label: "Chiropractors" },
      { value: "spa", label: "Spas" },
      { value: "wellness centres", label: "Wellness Centres" },
    ]
  },
  {
    label: "Personal Services",
    items: [
      { value: "florists", label: "Florists" },
      { value: "dog walkers", label: "Dog Walkers" },
      { value: "mobile car wash", label: "Mobile Car Wash" },
      { value: "tutors", label: "Tutors" },
      { value: "nail salons", label: "Nail Salons" },
      { value: "barbers", label: "Barbers" },
      { value: "hair salons", label: "Hair Salons" },
      { value: "pet grooming", label: "Pet Grooming" },
    ]
  },
  {
    label: "Attractions & Tourism",
    items: [
      { value: "theme parks", label: "Theme Parks" },
      { value: "zoos", label: "Zoos" },
      { value: "safari parks", label: "Safari Parks" },
      { value: "museums", label: "Museums" },
      { value: "parks", label: "Parks" },
      { value: "royal parks", label: "Royal Parks" },
      { value: "stately homes", label: "Stately Homes" },
      { value: "national trust properties", label: "National Trust Properties" },
    ]
  },
  {
    label: "Professional Services",
    items: [
      { value: "dentists", label: "Dentists" },
      { value: "real estate agents", label: "Real Estate" },
      { value: "lawyers", label: "Lawyers" },
      { value: "accountants", label: "Accountants" },
    ]
  },
];

export const StatCard = ({ icon: Icon, label, value, accent = false }) => (
  <div 
    className={`stat-card p-4 md:p-6 transition-colors duration-300 ${accent ? 'glow-yellow' : ''}`}
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center gap-3 mb-2">
      <Icon className={`w-5 h-5 ${accent ? 'text-yellow-500' : 'text-green-500'}`} />
      <span className="text-xs font-mono uppercase tracking-wider text-zinc-500">{label}</span>
    </div>
    <div className={`text-3xl md:text-4xl font-mono font-bold ${accent ? 'text-yellow-500' : 'text-white'}`}>
      {value}
    </div>
  </div>
);

export const BusinessRow = ({ business, index, onCopy, onEmailFound, onSendEmail }) => {
  const [isSearchingEmail, setIsSearchingEmail] = useState(false);
  const [foundEmails, setFoundEmails] = useState(business.email ? [business.email] : []);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [manualEmail, setManualEmail] = useState("");
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  
  // Generate search URLs for social media when direct links aren't available
  const searchQuery = encodeURIComponent(business.name + " " + (business.address?.split(",")[1]?.trim() || ""));
  const fbSearchUrl = `https://www.facebook.com/search/top?q=${searchQuery}`;
  // Google search for Instagram profile since IG doesn't have direct search URLs
  const igSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(business.name + " instagram")}`;
  // Google search for email
  const emailSearchUrl = `https://www.google.com/search?q=${encodeURIComponent(business.name + " " + (business.address?.split(",")[1]?.trim() || "") + " email contact")}`;
  
  // Generate WhatsApp link from phone if not provided by backend
  const getWhatsAppLink = () => {
    if (business.whatsapp_link) return business.whatsapp_link;
    if (!business.phone) return null;
    const cleaned = business.phone.replace(/[^0-9+]/g, '');
    const formatted = cleaned.startsWith('+') ? cleaned.slice(1) : (cleaned.startsWith('1') ? cleaned : '1' + cleaned);
    return `https://wa.me/${formatted}`;
  };
  
  const whatsappLink = getWhatsAppLink();
  
  const handleFindEmail = async () => {
    setIsSearchingEmail(true);
    try {
      // Extract simpler location (city/town name) from address
      const addressParts = business.address?.split(",") || [];
      // Try to get a simple location - usually the city/town is in the middle parts
      let simpleLocation = "";
      if (addressParts.length >= 2) {
        // Get the second-to-last part (usually city) or just use first meaningful part
        simpleLocation = addressParts.slice(-3, -1).join(" ").trim();
        // If it's just a postcode, try to get an earlier part
        if (/^[A-Z]{1,2}\d/.test(simpleLocation.trim())) {
          simpleLocation = addressParts.slice(0, 2).join(" ").trim();
        }
      }
      
      const response = await axios.post(`${API}/find-email`, {
        business_name: business.name,
        website: business.website || null,
        location: simpleLocation || ""
      });
      
      console.log("Email search response:", response.data);
      
      if (response.data.success && response.data.emails.length > 0) {
        setFoundEmails(response.data.emails);
        toast.success(`Found: ${response.data.emails[0]}`);
        if (onEmailFound) {
          onEmailFound(business.id, response.data.emails);
        }
      } else {
        toast.error("No email found for this business");
      }
    } catch (error) {
      console.error("Email search error:", error);
      toast.error("Failed to search for email: " + (error.response?.data?.detail || error.message));
    } finally {
      setIsSearchingEmail(false);
    }
  };

  const handleManualEmailSave = () => {
    const email = manualEmail.trim().toLowerCase();
    if (email && email.includes('@') && email.includes('.')) {
      setFoundEmails([email, ...foundEmails.filter(e => e !== email)]);
      setIsEditingEmail(false);
      setManualEmail("");
      toast.success(`Email added: ${email}`);
      if (onEmailFound) {
        onEmailFound(business.id, [email]);
      }
    } else {
      toast.error("Please enter a valid email address");
    }
  };

  const handleManualEmailKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleManualEmailSave();
    } else if (e.key === 'Escape') {
      setIsEditingEmail(false);
      setManualEmail("");
    }
  };

  const handleSendEmailClick = () => {
    const emailToSend = foundEmails[0] || business.email;
    if (!emailToSend) {
      toast.error("No email address available");
      return;
    }
    if (onSendEmail) {
      onSendEmail(business, emailToSend);
    }
  };
  
  const handleCopyRow = async () => {
    const data = formatBusinessForCopy({...business, email: foundEmails[0] || business.email});
    try {
      await navigator.clipboard.writeText(data);
      toast.success(`Copied ${business.name} to clipboard`);
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = data;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        toast.success(`Copied ${business.name} to clipboard`);
      } catch (e) {
        toast.error("Failed to copy");
      }
      document.body.removeChild(textarea);
    }
  };

  const handleCopyLink = async (url, platform) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`${platform} link copied! Paste in browser.`);
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        toast.success(`${platform} link copied! Paste in browser.`);
      } catch (e) {
        toast.error("Failed to copy link");
      }
      document.body.removeChild(textarea);
    }
  };
  
  const handleCopyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success(`Email copied: ${email}`);
    } catch (err) {
      toast.error("Failed to copy email");
    }
  };
  
  return (
    <TableRow className={`table-row-hover border-b border-zinc-800 ${!business.has_website ? 'bg-yellow-500/5' : ''}`} data-testid={`business-row-${index}`}>
      <TableCell className="font-medium text-white max-w-[200px]">
        <div className="flex items-start gap-3">
          {business.thumbnail && (
            <img 
              src={business.thumbnail} 
              alt="" 
              className="w-10 h-10 object-cover flex-shrink-0"
            />
          )}
          <div className="min-w-0">
            {business.has_website && business.website ? (
              <a 
                href={business.website}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate font-semibold hover:text-green-400 transition-colors flex items-center gap-1 group"
                title={`Visit: ${business.website}`}
              >
                {business.name}
                <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </a>
            ) : (
              <div className="truncate font-semibold">{business.name}</div>
            )}
            {business.category && (
              <div className="text-xs text-zinc-500 truncate">{business.category}</div>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="text-zinc-400 max-w-[200px]">
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-zinc-600" />
          <span className="truncate">{business.address || "N/A"}</span>
        </div>
      </TableCell>
      <TableCell className="text-zinc-400">
        <div className="flex items-center gap-2">
          <Phone className="w-4 h-4 text-zinc-600" />
          <span className="font-mono text-sm">{business.phone || "N/A"}</span>
        </div>
      </TableCell>
      <TableCell>
        {business.has_website ? (
          <Badge className="badge-has-site font-mono text-xs rounded-none">
            <Globe className="w-3 h-3 mr-1" />
            HAS SITE
          </Badge>
        ) : (
          <Badge className="badge-lead font-mono text-xs rounded-none animate-pulse">
            <WifiOff className="w-3 h-3 mr-1" />
            LEAD
          </Badge>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          {/* Email */}
          {isEditingEmail ? (
            <div className="flex items-center gap-1">
              <input
                type="email"
                value={manualEmail}
                onChange={(e) => setManualEmail(e.target.value)}
                onKeyDown={handleManualEmailKeyDown}
                placeholder="Enter email..."
                className="w-32 px-2 py-1 bg-zinc-900 border border-green-500/50 text-xs font-mono text-white focus:outline-none focus:border-green-400"
                autoFocus
              />
              <button
                onClick={handleManualEmailSave}
                className="p-1 bg-green-600/20 border border-green-500/30 hover:bg-green-600/40 transition-colors"
                title="Save email"
              >
                <Check className="w-3 h-3 text-green-400" />
              </button>
              <button
                onClick={() => { setIsEditingEmail(false); setManualEmail(""); }}
                className="p-1 bg-red-600/20 border border-red-500/30 hover:bg-red-600/40 transition-colors"
                title="Cancel"
              >
                <X className="w-3 h-3 text-red-400" />
              </button>
            </div>
          ) : foundEmails.length > 0 ? (
            <div className="flex items-center gap-1">
              <button 
                onClick={() => handleCopyEmail(foundEmails[0])}
                className="inline-flex items-center gap-1 px-2 py-1 bg-green-600/20 border border-green-500/30 hover:bg-green-600/30 transition-colors text-xs font-mono text-green-400 max-w-[120px]"
                title={`Click to copy: ${foundEmails[0]}`}
              >
                <Mail className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">{foundEmails[0]}</span>
              </button>
              {foundEmails.length > 1 && (
                <span className="text-xs text-green-500">+{foundEmails.length - 1}</span>
              )}
              <button
                onClick={handleSendEmailClick}
                className="p-1 bg-cyan-600/20 border border-cyan-500/30 hover:bg-cyan-600/40 transition-colors"
                title="Send email to this business"
              >
                <Send className="w-3 h-3 text-cyan-400" />
              </button>
              <button
                onClick={() => setIsEditingEmail(true)}
                className="p-1 bg-zinc-800/50 border border-zinc-700 hover:border-blue-500/50 hover:bg-blue-600/10 transition-colors"
                title="Edit/add email"
              >
                <Edit2 className="w-3 h-3 text-zinc-500 hover:text-blue-400" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <button 
                onClick={handleFindEmail}
                disabled={isSearchingEmail}
                className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-800/50 border border-zinc-700 hover:border-green-500/50 hover:bg-green-600/10 transition-colors group disabled:opacity-50 text-xs font-mono"
                title="Find email (scrapes website + social media)"
              >
                {isSearchingEmail ? (
                  <>
                    <Loader2 className="w-3 h-3 text-green-400 animate-spin" />
                    <span className="text-zinc-400">Finding...</span>
                  </>
                ) : (
                  <>
                    <Mail className="w-3 h-3 text-zinc-600 group-hover:text-green-400" />
                    <span className="text-zinc-500 group-hover:text-green-400">Find</span>
                  </>
                )}
              </button>
              <button
                onClick={() => setIsEditingEmail(true)}
                className="inline-flex items-center gap-1 px-2 py-1 bg-zinc-800/50 border border-zinc-700 hover:border-blue-500/50 hover:bg-blue-600/10 transition-colors group text-xs font-mono"
                title="Manually add email"
              >
                <Plus className="w-3 h-3 text-zinc-600 group-hover:text-blue-400" />
                <span className="text-zinc-500 group-hover:text-blue-400">Add</span>
              </button>
            </div>
          )}
          {/* WhatsApp */}
          {whatsappLink ? (
            <button 
              onClick={() => handleCopyLink(whatsappLink, 'WhatsApp')}
              className="inline-flex items-center justify-center w-8 h-8 bg-emerald-600/20 border border-emerald-500/30 hover:bg-emerald-600/30 transition-colors"
              title="Copy WhatsApp link"
            >
              <MessageCircle className="w-4 h-4 text-emerald-400" />
            </button>
          ) : (
            <div className="w-8 h-8 flex items-center justify-center bg-zinc-800/50 border border-zinc-700">
              <MessageCircle className="w-4 h-4 text-zinc-700" />
            </div>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          {business.facebook_link ? (
            <a 
              href={business.facebook_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-8 h-8 bg-blue-600/20 border border-blue-500/30 hover:bg-blue-600/30 transition-colors"
              title="View Facebook Page"
            >
              <Facebook className="w-4 h-4 text-blue-400" />
            </a>
          ) : (
            <button 
              onClick={() => handleCopyLink(fbSearchUrl, 'Facebook')}
              className="inline-flex items-center justify-center w-8 h-8 bg-zinc-800/50 border border-zinc-700 hover:border-blue-500/50 hover:bg-blue-600/10 transition-colors group"
              title="Copy Facebook search link"
            >
              <Facebook className="w-4 h-4 text-zinc-600 group-hover:text-blue-400" />
            </button>
          )}
          {business.instagram_link ? (
            <a 
              href={business.instagram_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center w-8 h-8 bg-pink-600/20 border border-pink-500/30 hover:bg-pink-600/30 transition-colors"
              title="View Instagram Profile"
            >
              <Instagram className="w-4 h-4 text-pink-400" />
            </a>
          ) : (
            <button 
              onClick={() => handleCopyLink(igSearchUrl, 'Instagram')}
              className="inline-flex items-center justify-center w-8 h-8 bg-zinc-800/50 border border-zinc-700 hover:border-pink-500/50 hover:bg-pink-600/10 transition-colors group"
              title="Copy Instagram search link"
            >
              <Instagram className="w-4 h-4 text-zinc-600 group-hover:text-pink-400" />
            </button>
          )}
        </div>
      </TableCell>
      <TableCell className="text-zinc-400">
        {business.rating ? (
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
            <span className="font-mono">{business.rating}</span>
            <span className="text-zinc-600 text-xs">({business.reviews_count || 0})</span>
          </div>
        ) : (
          <span className="text-zinc-600">N/A</span>
        )}
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCopyRow}
            className="inline-flex items-center justify-center w-8 h-8 bg-zinc-800/50 border border-zinc-700 hover:border-green-500/50 hover:bg-green-600/10 transition-colors group"
            title="Copy business info"
          >
            <Copy className="w-4 h-4 text-zinc-600 group-hover:text-green-400" />
          </button>
          {business.google_maps_link && (
            <a 
              href={business.google_maps_link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-green-500 hover:text-green-400 transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

// Format business data for clipboard - B2B Lead Schema
const formatBusinessForCopy = (business) => {
  // Generate WhatsApp link
  let whatsappLink = business.whatsapp_link;
  if (!whatsappLink && business.phone) {
    const cleaned = business.phone.replace(/[^0-9+]/g, '');
    const formatted = cleaned.startsWith('+') ? cleaned.slice(1) : (cleaned.startsWith('1') ? cleaned : '1' + cleaned);
    whatsappLink = `https://wa.me/${formatted}`;
  }
  
  // Normalize email
  const email = business.email ? business.email.toLowerCase().trim() : '';
  
  // Normalize phone to E.164
  const normalizePhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/[^0-9+]/g, '');
    if (cleaned.startsWith('+')) return cleaned;
    if (cleaned.length === 10) return '+1' + cleaned;
    if (cleaned.length === 11 && cleaned.startsWith('1')) return '+' + cleaned;
    return '+' + cleaned;
  };
  
  const e164Phone = normalizePhone(business.phone);
  
  // Confidence scoring
  const confidence = {
    email: email ? (business.email_source === 'website' ? 'High' : 'Med') : 'N/A',
    whatsapp: business.phone ? 'Med' : 'N/A',
    facebook: business.facebook_link ? 'High' : 'Low',
    instagram: business.instagram_link ? 'High' : 'Low'
  };
  
  const lines = [
    `CompanyName: ${business.name}`,
    `Location (City, Country): ${business.address || 'N/A'}`,
    `Category/Industry: ${business.category || 'N/A'}`,
    `HasWebsite: ${business.has_website ? 'Yes' : 'No'}`,
    `WebsiteURL: ${business.website || 'N/A'}`,
    `EmailAddresses: [${email || 'Not found'}]`,
    `Phone: ${e164Phone || 'N/A'}`,
    `WhatsApp: ${whatsappLink || 'N/A'}`,
    `FacebookURL: ${business.facebook_link || 'N/A'}`,
    `InstagramURL: ${business.instagram_link || 'N/A'}`,
    `SourceURLs: [${business.google_maps_link || 'Google Maps'}]`,
    `Notes: ${!business.has_website ? 'NO WEBSITE - HIGH PRIORITY LEAD' : 'Has existing website'}`,
    `FieldConfidence: {email: ${confidence.email}, whatsapp: ${confidence.whatsapp}, facebook: ${confidence.facebook}, instagram: ${confidence.instagram}}`,
  ];
  
  return lines.join('\n');
};

// Format all businesses for clipboard - B2B Lead Schema
const formatAllBusinessesForCopy = (businesses, leadsOnly = false) => {
  const filtered = leadsOnly ? businesses.filter(b => !b.has_website) : businesses;
  
  if (filtered.length === 0) return '';
  
  // Normalize phone to E.164
  const normalizePhone = (phone) => {
    if (!phone) return '';
    const cleaned = phone.replace(/[^0-9+]/g, '');
    if (cleaned.startsWith('+')) return cleaned;
    if (cleaned.length === 10) return '+1' + cleaned;
    if (cleaned.length === 11 && cleaned.startsWith('1')) return '+' + cleaned;
    return '+' + cleaned;
  };
  
  const header = `=== B2B LEAD EXPORT ===\nTotal Leads: ${filtered.length}\nGenerated: ${new Date().toISOString()}\n${'='.repeat(50)}\n\n`;
  
  const businessBlocks = filtered.map((b, i) => {
    // Generate WhatsApp link
    let whatsappLink = b.whatsapp_link;
    if (!whatsappLink && b.phone) {
      const cleaned = b.phone.replace(/[^0-9+]/g, '');
      const formatted = cleaned.startsWith('+') ? cleaned.slice(1) : (cleaned.startsWith('1') ? cleaned : '1' + cleaned);
      whatsappLink = `https://wa.me/${formatted}`;
    }
    
    const email = b.email ? b.email.toLowerCase().trim() : '';
    const e164Phone = normalizePhone(b.phone);
    
    const confidence = {
      email: email ? 'Med' : 'N/A',
      whatsapp: b.phone ? 'Med' : 'N/A',
      facebook: b.facebook_link ? 'High' : 'Low',
      instagram: b.instagram_link ? 'High' : 'Low'
    };
    
    const lines = [
      `--- LEAD #${i + 1} ---`,
      `CompanyName: ${b.name}`,
      `Location (City, Country): ${b.address || 'N/A'}`,
      `Category/Industry: ${b.category || 'N/A'}`,
      `HasWebsite: ${b.has_website ? 'Yes' : 'No'}`,
      `WebsiteURL: ${b.website || 'N/A'}`,
      `EmailAddresses: [${email || 'Not found - click Find Email'}]`,
      `Phone: ${e164Phone || 'N/A'}`,
      `WhatsApp: ${whatsappLink || 'N/A'}`,
      `FacebookURL: ${b.facebook_link || 'Search: https://www.facebook.com/search/top?q=' + encodeURIComponent(b.name)}`,
      `InstagramURL: ${b.instagram_link || 'Search: https://www.google.com/search?q=' + encodeURIComponent(b.name + ' instagram')}`,
      `SourceURLs: [${b.google_maps_link || 'Google Maps'}]`,
      `Notes: ${!b.has_website ? 'NO WEBSITE - HIGH PRIORITY LEAD' : 'Has existing website'}`,
      `FieldConfidence: {email: ${confidence.email}, whatsapp: ${confidence.whatsapp}, facebook: ${confidence.facebook}, instagram: ${confidence.instagram}}`,
    ];
    return lines.join('\n');
  });
  
  return header + businessBlocks.join('\n\n');
};

function App() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  
  // Search state
  const [niche, setNiche] = useState("");
  const [location, setLocation] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [businesses, setBusinesses] = useState([]);
  const [searchId, setSearchId] = useState(null);
  const [filterNoWebsite, setFilterNoWebsite] = useState(false);
  const [stats, setStats] = useState({ total: 0, noWebsite: 0, hasSocial: 0, hasEmail: 0 });
  const [isBulkFinding, setIsBulkFinding] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });
  const [showBulkEmail, setShowBulkEmail] = useState(false);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [sendEmailTarget, setSendEmailTarget] = useState({ business: null, email: null });
  const [totalEmailsSent, setTotalEmailsSent] = useState(null);
  const [searchMode, setSearchMode] = useState("business"); // "business" or "people"
  const [peopleResults, setPeopleResults] = useState([]);

  // Fetch total emails sent on mount
  useEffect(() => {
    const fetchTotalEmails = async () => {
      try {
        const response = await axios.get(`${API}/total-email-stats`);
        setTotalEmailsSent(response.data.total_sent);
      } catch (error) {
        console.error("Could not fetch email stats:", error);
      }
    };
    if (isAuthenticated) {
      fetchTotalEmails();
    }
  }, [isAuthenticated]);

  // Check for existing session on mount
  useEffect(() => {
    const savedToken = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      setIsAuthenticated(true);
      
      // Set default auth header for all requests
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedToken}`;
    }
  }, []);

  const handleLogin = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    setIsAuthenticated(true);
    axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
    delete axios.defaults.headers.common['Authorization'];
    toast.success("Logged out successfully");
  };

  const handleScrape = useCallback(async () => {
    if (!niche || !location) {
      toast.error("Please select a niche and enter a location");
      return;
    }

    // Detect if this is a people search niche
    const allItems = NICHE_GROUPS.flatMap(g => g.items);
    const selectedItem = allItems.find(item => item.value === niche);
    const isPeopleSearch = selectedItem?.mode === "people";

    setIsLoading(true);
    setProgress(0);
    setBusinesses([]);
    setPeopleResults([]);
    setSearchId(null);
    setSearchMode(isPeopleSearch ? "people" : "business");

    // Simulate progress
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 300);

    try {
      if (isPeopleSearch) {
        // People search mode
        const response = await axios.post(`${API}/people-search`, {
          role: niche,
          location,
          limit: 30
        });

        clearInterval(progressInterval);
        setProgress(100);

        const data = response.data;
        setPeopleResults(data.contacts || []);
        setSearchId(data.search_id);
        
        setStats({
          total: data.total_found,
          noWebsite: 0,
          hasSocial: 0,
          hasEmail: data.contacts?.filter(c => c.is_personal).length || 0
        });

        toast.success(`Found ${data.total_found} contacts for "${niche}" in ${location}`);
      } else {
        // Business search mode (original)
        const response = await axios.post(`${API}/scrape`, {
          niche,
          location,
          limit: 50
        });

        clearInterval(progressInterval);
        setProgress(100);

        const data = response.data;
        setBusinesses(data.businesses || []);
        setSearchId(data.search_id);
        
        const withSocial = (data.businesses || []).filter(b => 
          !b.has_website && (b.facebook_link || b.instagram_link)
        ).length;
        const withEmail = (data.businesses || []).filter(b => b.email).length;
        
        setStats({
          total: data.total_found,
          noWebsite: data.without_website,
          hasSocial: withSocial,
          hasEmail: withEmail
        });

        toast.success(`Found ${data.total_found} businesses, ${data.without_website} without websites!`);
      }
    } catch (error) {
      clearInterval(progressInterval);
      console.error("Scrape error:", error);
      
      // Handle auth errors
      if (error.response?.status === 401) {
        handleLogout();
        toast.error("Session expired. Please login again.");
        return;
      }
      
      toast.error(error.response?.data?.detail || "Failed to scrape data. Please try again.");
    } finally {
      setIsLoading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  }, [niche, location]);

  const handleExport = useCallback(async (format) => {
    if (!searchId) {
      toast.error("No data to export. Please run a search first.");
      return;
    }

    try {
      const endpoint = format === 'csv' ? 'csv' : 'excel';
      const response = await axios.get(`${API}/export/${endpoint}`, {
        params: { search_id: searchId },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `businesses.${format === 'csv' ? 'csv' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export data");
    }
  }, [searchId]);

  const handleCopyAll = useCallback(async (leadsOnly = false) => {
    const data = formatAllBusinessesForCopy(businesses, leadsOnly);
    if (!data) {
      toast.error("No data to copy");
      return;
    }
    
    try {
      await navigator.clipboard.writeText(data);
      const count = leadsOnly ? businesses.filter(b => !b.has_website).length : businesses.length;
      toast.success(`Copied ${count} businesses to clipboard`);
    } catch (err) {
      // Fallback: Create a textarea and copy
      const textarea = document.createElement('textarea');
      textarea.value = data;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        const count = leadsOnly ? businesses.filter(b => !b.has_website).length : businesses.length;
        toast.success(`Copied ${count} businesses to clipboard`);
      } catch (e) {
        toast.error("Failed to copy to clipboard");
      }
      document.body.removeChild(textarea);
    }
  }, [businesses]);

  // Update email for a business and recalculate stats
  const handleEmailFound = useCallback((businessId, emails) => {
    setBusinesses(prev => {
      const updated = prev.map(b => 
        b.id === businessId ? { ...b, email: emails[0] } : b
      );
      // Update email count
      const withEmail = updated.filter(b => b.email).length;
      setStats(s => ({ ...s, hasEmail: withEmail }));
      return updated;
    });
  }, []);

  // Quick Find - Fast email search for a single business
  const handleQuickFind = useCallback(async (business) => {
    if (!business) {
      toast.info("No businesses without email to search");
      return [];
    }
    toast.loading(`Searching for email: ${business.name}...`, { id: "quickfind" });
    try {
      const response = await axios.post(`${API}/find-email`, {
        business_name: business.name,
        website: business.website || null,
        location: business.address?.split(",").slice(-3, -1).join(" ").trim() || ""
      });
      
      if (response.data.success && response.data.emails.length > 0) {
        handleEmailFound(business.id, response.data.emails);
        toast.success(`Found: ${response.data.emails[0]}`, { id: "quickfind" });
        return response.data.emails;
      }
      toast.error(`No email found for ${business.name}`, { id: "quickfind" });
      return [];
    } catch (error) {
      console.error("Quick find error:", error);
      toast.error(`Search failed: ${error.message}`, { id: "quickfind" });
      return [];
    }
  }, [handleEmailFound]);

  // Deep Find - More thorough email search
  const handleDeepFind = useCallback(async (business) => {
    if (!business) {
      toast.info("No businesses without email to search");
      return [];
    }
    toast.loading(`Deep searching for email: ${business.name}...`, { id: "deepfind" });
    try {
      // First try quick find
      const quickEmails = await handleQuickFind(business);
      if (quickEmails.length > 0) {
        toast.success(`Found: ${quickEmails[0]}`, { id: "deepfind" });
        return quickEmails;
      }
      
      // If no results, try with different search terms
      const response = await axios.post(`${API}/find-email`, {
        business_name: business.name,
        website: business.website || null,
        location: business.address || ""
      });
      
      if (response.data.success && response.data.emails.length > 0) {
        handleEmailFound(business.id, response.data.emails);
        toast.success(`Found: ${response.data.emails[0]}`, { id: "deepfind" });
        return response.data.emails;
      }
      toast.error(`No email found for ${business.name}`, { id: "deepfind" });
      return [];
    } catch (error) {
      console.error("Deep find error:", error);
      toast.error(`Search failed: ${error.message}`, { id: "deepfind" });
      return [];
    }
  }, [handleQuickFind, handleEmailFound]);

  // Bulk Email - Find emails for all businesses without email
  const handleBulkEmail = useCallback(async () => {
    const businessesNeedingEmail = businesses.filter(b => !b.email);
    
    if (businessesNeedingEmail.length === 0) {
      toast.info("All businesses already have emails");
      return;
    }
    
    setIsBulkFinding(true);
    setBulkProgress({ current: 0, total: businessesNeedingEmail.length });
    
    let found = 0;
    
    for (let i = 0; i < businessesNeedingEmail.length; i++) {
      const business = businessesNeedingEmail[i];
      setBulkProgress({ current: i + 1, total: businessesNeedingEmail.length });
      
      try {
        const emails = await handleQuickFind(business);
        if (emails.length > 0) found++;
      } catch (error) {
        console.error(`Failed to find email for ${business.name}:`, error);
      }
      
      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    setIsBulkFinding(false);
    setBulkProgress({ current: 0, total: 0 });
    toast.success(`Bulk search complete! Found ${found} new emails`);
  }, [businesses, handleQuickFind]);

  // Handler for sending email to individual business
  const handleSendEmailToBusiness = useCallback((business, email) => {
    setSendEmailTarget({ business, email });
    setShowSendEmail(true);
  }, []);

  const filteredBusinesses = filterNoWebsite 
    ? businesses.filter(b => !b.has_website)
    : businesses;

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return (
      <>
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#18181b',
              border: '1px solid #27272a',
              color: '#fafafa',
              fontFamily: 'IBM Plex Sans, sans-serif'
            }
          }}
        />
        <Login onLogin={handleLogin} />
      </>
    );
  }

  return (
    <div className="App min-h-screen">
      <Toaster 
        position="top-right" 
        toastOptions={{
          style: {
            background: '#18181b',
            border: '1px solid #27272a',
            color: '#fafafa',
            fontFamily: 'IBM Plex Sans, sans-serif'
          }
        }}
      />
      
      {/* Admin Panel Modal */}
      {showAdminPanel && (
        <AdminPanel token={token} onClose={() => setShowAdminPanel(false)} />
      )}
      
      {/* Bulk Email Campaign Modal */}
      {showBulkEmail && (
        <BulkEmailCampaign 
          isOpen={showBulkEmail} 
          onClose={() => setShowBulkEmail(false)} 
          businesses={businesses}
          stats={stats}
        />
      )}
      
      {/* Send Email Modal (Individual) */}
      {showSendEmail && (
        <SendEmailModal
          isOpen={showSendEmail}
          onClose={() => {
            setShowSendEmail(false);
            setSendEmailTarget({ business: null, email: null });
          }}
          business={sendEmailTarget.business}
          recipientEmail={sendEmailTarget.email}
        />
      )}
      
      {/* Header */}
      <header className="header-gradient border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 border border-green-500/30">
                <Terminal className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <h1 className="text-xl md:text-2xl font-mono font-bold text-white tracking-tight">
                  SEARCH<span className="text-green-500">A</span>LEAD
                </h1>
                <p className="text-xs text-zinc-500 font-mono">
                  B2B Lead Generation Platform
                </p>
              </div>
            </div>
            
            {/* User Info & Actions */}
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => setShowBulkEmail(true)}
                className="rounded-none border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 font-mono text-xs relative"
                data-testid="campaigns-header-button"
              >
                <Mail className="w-4 h-4 mr-2" />
                CAMPAIGNS
                {totalEmailsSent !== null && totalEmailsSent > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 bg-cyan-500 text-black text-xs font-bold rounded">
                    {totalEmailsSent}
                  </span>
                )}
              </Button>
              {user?.is_admin && (
                <Button
                  variant="outline"
                  onClick={() => setShowAdminPanel(true)}
                  className="rounded-none border-yellow-500/50 text-yellow-500 hover:bg-yellow-500/10 font-mono text-xs"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  ADMIN
                </Button>
              )}
              <div className="flex items-center gap-2 px-3 py-2 bg-zinc-800/50 border border-zinc-700">
                <User className="w-4 h-4 text-zinc-500" />
                <span className="text-sm font-mono text-zinc-300">{user?.username}</span>
              </div>
              <Button
                variant="ghost"
                onClick={handleLogout}
                className="rounded-none text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Command Center */}
        <section className="mb-8" data-testid="search-section">
          <div className="terminal-card p-6 pt-12">
            {/* Terminal header dots */}
            <div className="absolute top-2.5 left-4 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-red-500/70"></div>
              <div className="w-3 h-3 rounded-full bg-yellow-500/70"></div>
              <div className="w-3 h-3 rounded-full bg-green-500/70"></div>
              <span className="ml-4 text-xs font-mono text-zinc-600">search_command</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
              {/* Niche Select */}
              <div className="md:col-span-4">
                <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2 block">
                  Business Niche
                </Label>
                <Select value={niche} onValueChange={setNiche}>
                  <SelectTrigger 
                    className="bg-zinc-900 border-zinc-700 rounded-none h-12 font-mono"
                    data-testid="niche-select"
                  >
                    <SelectValue placeholder="Select niche..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-700 rounded-none max-h-[300px]">
                    {NICHE_GROUPS.map(group => (
                      <SelectGroup key={group.label}>
                        <SelectLabel className="text-yellow-500 font-mono text-xs uppercase tracking-wider px-2 py-2 bg-zinc-800/50">
                          {group.label}
                        </SelectLabel>
                        {group.items.map(item => (
                          <SelectItem 
                            key={item.value} 
                            value={item.value}
                            className="font-mono hover:bg-zinc-800 focus:bg-zinc-800 pl-4"
                          >
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Location Input */}
              <div className="md:col-span-4">
                <Label className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2 block">
                  Location
                </Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                  <Input
                    type="text"
                    placeholder="e.g., Los Angeles, CA"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    className="bg-zinc-900 border-zinc-700 rounded-none h-12 pl-10 font-mono"
                    data-testid="location-input"
                  />
                </div>
              </div>

              {/* Search Button */}
              <div className="md:col-span-4">
                <Button
                  onClick={handleScrape}
                  disabled={isLoading || !niche || !location}
                  className="w-full h-12 bg-green-500 hover:bg-green-600 text-black font-mono uppercase tracking-wider rounded-none btn-cyber glow-green"
                  data-testid="search-button"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      SCANNING...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4 mr-2" />
                      {(() => {
                        const allItems = NICHE_GROUPS.flatMap(g => g.items);
                        const selected = allItems.find(item => item.value === niche);
                        return selected?.mode === "people" ? "FIND CONTACTS" : "SCAN BUSINESSES";
                      })()}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Progress Bar */}
            {isLoading && (
              <div className="mt-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono text-zinc-500">SCANNING GOOGLE MAPS...</span>
                  <span className="text-xs font-mono text-green-500">{Math.round(progress)}%</span>
                </div>
                <Progress value={progress} className="h-2 rounded-none bg-zinc-800" />
              </div>
            )}
          </div>
        </section>

        {/* Stats Cards */}
        {businesses.length > 0 && (
          <section className="mb-8 grid grid-cols-2 md:grid-cols-6 gap-4">
            <StatCard icon={Database} label="Total Found" value={stats.total} />
            <StatCard icon={WifiOff} label="No Website" value={stats.noWebsite} accent />
            <StatCard icon={Globe} label="Has Website" value={stats.total - stats.noWebsite} />
            <StatCard icon={Mail} label="Has Email" value={stats.hasEmail} />
            <StatCard icon={Facebook} label="Has Social" value={stats.hasSocial} />
            <StatCard 
              icon={CheckCircle2} 
              label="Lead Rate" 
              value={`${stats.total > 0 ? Math.round((stats.noWebsite / stats.total) * 100) : 0}%`} 
              accent 
            />
          </section>
        )}

        {/* Results Section */}
        {businesses.length > 0 && (
          <section data-testid="results-section">
            {/* Controls Bar */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4 p-4 bg-zinc-900/50 border border-zinc-800">
              {/* Filter Toggle */}
              <div className="flex items-center gap-3">
                <Switch
                  id="filter-no-website"
                  checked={filterNoWebsite}
                  onCheckedChange={setFilterNoWebsite}
                  className="data-[state=checked]:bg-yellow-500"
                  data-testid="filter-toggle"
                />
                <Label 
                  htmlFor="filter-no-website" 
                  className="text-sm font-mono cursor-pointer flex items-center gap-2"
                >
                  <Filter className="w-4 h-4 text-yellow-500" />
                  Show only leads (no website)
                </Label>
                {filterNoWebsite && (
                  <Badge className="badge-lead font-mono text-xs rounded-none">
                    {filteredBusinesses.length} LEADS
                  </Badge>
                )}
              </div>

              {/* Export & Copy Buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Email Finding Buttons */}
                <Button
                  variant="outline"
                  onClick={() => {
                    const businessesWithoutEmail = filteredBusinesses.filter(b => !b.email);
                    if (businessesWithoutEmail.length > 0) {
                      handleQuickFind(businessesWithoutEmail[0]);
                    } else {
                      toast.info("All businesses already have emails");
                    }
                  }}
                  disabled={isBulkFinding}
                  className="rounded-none border-green-500/50 bg-green-500/10 hover:bg-green-500/20 text-green-400 font-mono text-xs"
                  data-testid="quick-find-button"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  QUICK FIND
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    const businessesWithoutEmail = filteredBusinesses.filter(b => !b.email);
                    if (businessesWithoutEmail.length > 0) {
                      handleDeepFind(businessesWithoutEmail[0]);
                    } else {
                      toast.info("All businesses already have emails");
                    }
                  }}
                  disabled={isBulkFinding}
                  className="rounded-none border-orange-500/50 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 font-mono text-xs"
                  data-testid="deep-find-button"
                >
                  <Search className="w-4 h-4 mr-2" />
                  DEEP FIND
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowBulkEmail(true)}
                  className="rounded-none border-cyan-500/50 bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400 font-mono text-xs relative"
                  data-testid="bulk-email-button"
                >
                  <Send className="w-4 h-4 mr-2" />
                  BULK EMAIL
                  {totalEmailsSent !== null && totalEmailsSent > 0 && (
                    <span className="ml-2 px-1.5 py-0.5 bg-cyan-500 text-black text-xs font-bold rounded">
                      {totalEmailsSent}
                    </span>
                  )}
                </Button>
                
                <div className="w-px h-6 bg-zinc-700 mx-1 hidden sm:block"></div>
                
                {/* Copy Buttons */}
                <Button
                  variant="outline"
                  onClick={() => handleCopyAll(true)}
                  disabled={stats.noWebsite === 0}
                  className="rounded-none border-yellow-500/50 hover:bg-yellow-500/10 hover:text-yellow-500 font-mono text-xs"
                  data-testid="copy-leads-button"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  COPY LEADS ({stats.noWebsite})
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleCopyAll(false)}
                  className="rounded-none border-zinc-700 hover:bg-zinc-800 font-mono text-xs"
                  data-testid="copy-all-button"
                >
                  <ClipboardCheck className="w-4 h-4 mr-2" />
                  COPY ALL
                </Button>
                
                <div className="w-px h-6 bg-zinc-700 mx-1 hidden sm:block"></div>
                
                {/* Export Buttons */}
                <Button
                  variant="outline"
                  onClick={() => handleExport('csv')}
                  className="rounded-none border-zinc-700 hover:bg-zinc-800 font-mono text-xs"
                  data-testid="export-csv-button"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleExport('excel')}
                  className="rounded-none border-zinc-700 hover:bg-zinc-800 font-mono text-xs"
                  data-testid="export-excel-button"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  EXCEL
                </Button>
              </div>
            </div>

            {/* Results Table */}
            <div className="table-wrapper border border-zinc-800">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="border-b border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-500">Business</TableHead>
                    <TableHead className="text-zinc-500">Address</TableHead>
                    <TableHead className="text-zinc-500">Phone</TableHead>
                    <TableHead className="text-zinc-500">Website</TableHead>
                    <TableHead className="text-zinc-500">Contact</TableHead>
                    <TableHead className="text-zinc-500">Social</TableHead>
                    <TableHead className="text-zinc-500">Rating</TableHead>
                    <TableHead className="text-zinc-500">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBusinesses.map((business, index) => (
                    <BusinessRow 
                      key={business.id} 
                      business={business} 
                      index={index}
                      onEmailFound={handleEmailFound}
                      onSendEmail={handleSendEmailToBusiness}
                    />
                  ))}
                </TableBody>
              </Table>

              {filteredBusinesses.length === 0 && (
                <div className="no-results p-12 text-center">
                  <XCircle className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-500 font-mono">No businesses match the current filter</p>
                </div>
              )}
            </div>

            {/* Results Count */}
            <div className="mt-4 text-xs font-mono text-zinc-600 text-right">
              Showing {filteredBusinesses.length} of {businesses.length} results
            </div>
          </section>
        )}

        {/* People Search Results */}
        {!isLoading && searchMode === "people" && peopleResults.length > 0 && (
          <section className="mb-8" data-testid="people-results">
            {/* People Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
              <div className="stat-card p-4 bg-zinc-900/80 border border-zinc-800">
                <div className="text-2xl font-mono font-bold text-white">{peopleResults.length}</div>
                <div className="text-xs text-zinc-500 mt-1">Contacts Found</div>
              </div>
              <div className="stat-card p-4 bg-zinc-900/80 border border-green-500/20">
                <div className="text-2xl font-mono font-bold text-green-400">{peopleResults.filter(c => c.is_personal).length}</div>
                <div className="text-xs text-zinc-500 mt-1">Named Contacts</div>
              </div>
              <div className="stat-card p-4 bg-zinc-900/80 border border-cyan-500/20">
                <div className="text-2xl font-mono font-bold text-cyan-400">{new Set(peopleResults.map(c => c.organization)).size}</div>
                <div className="text-xs text-zinc-500 mt-1">Organizations</div>
              </div>
            </div>

            {/* Copy All Emails */}
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-mono text-zinc-400">
                {peopleResults.length} contacts for <span className="text-green-400">{niche}</span> in <span className="text-cyan-400">{location}</span>
              </span>
              <button
                onClick={() => {
                  const emails = peopleResults.map(c => `${c.name ? c.name + ' - ' : ''}${c.email} (${c.organization})`).join('\n');
                  navigator.clipboard.writeText(emails);
                  toast.success(`Copied ${peopleResults.length} contacts`);
                }}
                className="px-3 py-1.5 text-xs font-mono bg-green-500 text-black font-bold hover:bg-green-400 transition-colors"
                data-testid="copy-people-results"
              >
                COPY ALL CONTACTS
              </button>
            </div>

            {/* People Table */}
            <div className="table-wrapper border border-zinc-800">
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="border-b border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-500">Name</TableHead>
                    <TableHead className="text-zinc-500">Email</TableHead>
                    <TableHead className="text-zinc-500">Organization</TableHead>
                    <TableHead className="text-zinc-500">Role</TableHead>
                    <TableHead className="text-zinc-500">Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {peopleResults.map((contact, index) => (
                    <TableRow key={contact.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                      <TableCell className="font-mono text-white text-sm">
                        {contact.name || <span className="text-zinc-600">Unknown</span>}
                      </TableCell>
                      <TableCell>
                        <a href={`mailto:${contact.email}`} className="text-green-400 font-mono text-sm hover:text-green-300">
                          {contact.email}
                        </a>
                      </TableCell>
                      <TableCell className="text-zinc-400 text-sm">{contact.organization}</TableCell>
                      <TableCell className="text-zinc-500 text-sm capitalize">{contact.role}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-0.5 text-xs font-mono ${
                          contact.is_personal ? 'bg-green-500/20 text-green-400' : 'bg-cyan-500/20 text-cyan-400'
                        }`}>
                          {contact.is_personal ? 'NAMED' : 'DEPT'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 text-xs font-mono text-zinc-600 text-right">
              Showing {peopleResults.length} contacts
            </div>
          </section>
        )}

        {/* Empty State */}
        {!isLoading && businesses.length === 0 && peopleResults.length === 0 && (
          <section className="text-center py-20">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-zinc-900 border border-zinc-800 mb-6">
              <Search className="w-10 h-10 text-zinc-700" />
            </div>
            <h2 className="text-xl font-mono text-zinc-400 mb-2">No Data Yet</h2>
            <p className="text-zinc-600 max-w-md mx-auto">
              Select a business niche and location above, then click SCAN BUSINESSES to find leads without websites.
            </p>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between text-xs font-mono text-zinc-600">
            <span>LEAD_FINDER v1.0 // Powered by SerpAPI</span>
            <span className="flex items-center gap-2">
              <span className="status-dot online"></span>
              SYSTEM ONLINE
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
